import { easeOut, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Footer } from "../components/Footer";
import blindDateLogo from "../assets/blind-date-with-text-logo.webp";
import storiesBlueBackgorund from "../assets/stories-blue-background.webp";
import blurredPerson from "../assets/blurred-persons.webp";
import coupleBlackRed from "../assets/coupleblackred.webp";
import dateCafe from "../assets/couple-cafe.webp";
import { ArrowRightIcon } from "../common/icons/icons";
import { howItWorks } from "../data/howItWorks";
import { testimonials } from "../data/testimonials";
import { PolaroidCard } from "../components/PolaroidCard";
import bgMoments from "../assets/gradient-background.webp";
import polaroidCafe from "../assets/couple-cafe.webp";
import polaroidChilling from "../assets/couple-chilling.webp";
import polaroidClose from "../assets/two-heads.webp";
import eventBanner from "../assets/website banner.webp";
import { EventBanner } from "../components/EventBanner";

const HomePage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: easeOut },
    },
  };

  return (
    <main className="min-h-screen bg-background overflow-hidden">
      <EventBanner imageSrc={eventBanner} pdfUrl="/event-info.pdf" />
      {/* Hero Section */}
      <motion.section
        className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 pt-20 pb-20"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Background Accent */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

        <motion.div
          className="text-center max-w-3xl mx-auto relative z-10"
          variants={containerVariants}
        >
          <motion.img
            src={blindDateLogo}
            alt="The Blind Date Logo"
            className="w-[260px] sm:w-[340px] lg:w-[420px] mx-auto mb-6"
            variants={itemVariants}
          />

          <motion.p
            className="text-xl sm:text-2xl text-foreground/70 mb-4 font-light"
            variants={itemVariants}
          >
            <span className="tracking-widest">
              blind date | real conversations
            </span>
          </motion.p>

          <motion.p
            className="text-lg sm:text-xl text-foreground/60 mb-12 leading-relaxed max-w-2xl mx-auto"
            variants={itemVariants}
          >
            Your journey to something unexpected begins here. Discover, connect,
            and maybe find a spark. Explore the Blind Date experience now.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center w-full max-w-xs mx-auto sm:max-w-none"
            variants={itemVariants}
          >
            <Link to={"/city-selection"}>
              <motion.button
                className="w-full sm:w-auto px-10 py-4 bg-primary text-primary-foreground rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 group"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Your Journey
                <ArrowRightIcon />
              </motion.button>
            </Link>
            <Link to="/how-it-works">
              <motion.button
                className="w-full sm:w-auto px-10 py-4 border-2 border-primary text-primary rounded-lg font-semibold text-lg hover:bg-primary/10 transition-all duration-300 flex items-center gap-2"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                How It Works
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </motion.section>
      {/* ===== SOME STORIES SECTION ===== */}
      <section className="relative w-full">
        <img
          src={storiesBlueBackgorund}
          alt="Some stories"
          className="w-full h-[70vh] md:h-[90vh] object-cover object-center"
        />
      </section>

      {/* ===== BEGIN THE CONNECTION SECTION ===== */}
      <section className="relative w-full">
        {/* FLEX CONTAINER FOR IMAGES */}
        <div className="flex flex-col md:flex-row w-full">
          {/* LEFT IMAGE */}
          <div
            className="
      w-full md:w-1/2 
      h-[45vh] sm:h-[55vh] md:h-[90vh] 
      overflow-hidden
    "
          >
            <img
              src={blurredPerson}
              alt="Blur silhouettes"
              className="w-full h-full object-cover object-center"
            />
          </div>

          {/* RIGHT IMAGE */}
          <div
            className="
      w-full md:w-1/2 
      h-[45vh] sm:h-[55vh] md:h-[90vh] 
      overflow-hidden
    "
          >
            <img
              src={coupleBlackRed}
              alt="Couple holding rose"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>

        {/* PERFECT CENTER FLOATING BUTTON */}
        <div
          className="
    absolute inset-0
    flex items-center justify-center
    pointer-events-none md:pointer-events-auto
  "
        >
          <Link to={"/city-selection"}>
            <button
              className="
          pointer-events-auto
          bg-primary text-primary-foreground
          px-6 sm:px-10 lg:px-14
          py-3 sm:py-4 lg:py-5
          rounded-[40px]
          text-base sm:text-lg md:text-xl lg:text-2xl
          font-medium shadow-xl tracking-wide
          hover:scale-[1.03] transition-all duration-300
          whitespace-nowrap
        "
            >
              Begin the connection &gt;
            </button>
          </Link>
        </div>
      </section>

      {/* How It Works Section */}
      <motion.section
        className="relative py-24 px-4 sm:px-6 lg:px-8 bg-white/50"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="max-w-6xl mx-auto">
          <motion.h2
            className="text-4xl sm:text-5xl font-light text-center mb-20 text-foreground italic"
            variants={itemVariants}
          >
            How It Works
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  className="relative"
                  variants={itemVariants}
                  whileHover={{ y: -8 }}
                >
                  <div className="bg-card rounded-lg p-8 h-full shadow-sm hover:shadow-md transition-shadow border border-border">
                    <div className="absolute -top-5 -right-5 w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                      {item.step}
                    </div>
                    <div className="text-primary text-4xl mb-4 w-10 h-10">
                      <Icon />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-foreground/60 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* ===== STORY SECTION ===== */}
      <section className="w-full py-16 px-6 sm:px-10 lg:px-20 bg-background">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* LEFT TEXT BLOCK */}
          <div>
            <p className="text-foreground text-lg leading-relaxed mb-8">
              You fill it out, We take it from there. We don’t match you by
              looks, bios, or photos. We match you by energy, answers, and a
              dash of instinct.
              <br />
              <br />
              Then we set you up for a real, in-person date. No screens. No
              pressure. Just two people, one table, and a chance to be seen for
              who you are, Not how you appear.
              <br />
              <br />
              Because we believe stories start better offline. And sometimes…
              the unknown is the most exciting part.
            </p>

            <p className="text-primary text-xl sm:text-2xl font-semibold leading-snug">
              No apps. No swipes.
              <br />
              Just one form and a<br />
              little leap of faith &gt;&gt;&gt;
            </p>
          </div>

          {/* RIGHT IMAGE BLOCK */}
          <div className="flex flex-col items-center">
            <img
              src={dateCafe}
              alt="Two people on a date"
              className="w-full rounded-[32px] shadow-md object-cover"
            />

            <p className="mt-4 text-foreground font-semibold text-2xl text-center">
              Beyond profiles, beyond bios, just presence
            </p>
          </div>
        </div>
      </section>

      {/* ===== NOT A MATCH – MOMENTS SECTION ===== */}
      <section
        className="relative w-full 
  min-h-[70vh] sm:min-h-[80vh] lg:min-h-[95vh]
  flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <img
          src={bgMoments}
          alt="Pink gradient"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Mobile gradient overlay for readability */}
        <div className="absolute inset-0 bg-black/10 sm:bg-transparent"></div>

        {/* CONTENT */}
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-4">
          {/* Mobile-friendly text */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="
        text-white 
        text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl
        font-light italic 
        leading-snug sm:leading-tight 
        tracking-wide sm:tracking-normal
        drop-shadow-lg mt-10 sm:mt-0
      "
          >
            Not a match.
            <br className="sm:hidden" /> A meeting of moments.
          </motion.h2>

          {/* Polaroids — Desktop layout */}
          <div className="hidden md:block">
            {/* Left */}
            <motion.div
              initial={{ opacity: 0, x: -80, rotate: -15 }}
              whileInView={{ opacity: 1, x: 0, rotate: -10 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="absolute left-10 top-24"
            >
              <PolaroidCard src={polaroidCafe} alt="Cafe moment" />
            </motion.div>

            {/* Right */}
            <motion.div
              initial={{ opacity: 0, x: 80, rotate: 15 }}
              whileInView={{ opacity: 1, x: 0, rotate: 10 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.1 }}
              className="absolute right-10 top-28"
            >
              <PolaroidCard src={polaroidClose} alt="Close moment" />
            </motion.div>

            {/* Bottom */}
            <motion.div
              initial={{ opacity: 0, y: 80, rotate: 12 }}
              whileInView={{ opacity: 1, y: 0, rotate: 6 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, delay: 0.15 }}
              className="absolute bottom-14 left-1/2 -translate-x-1/2"
            >
              <PolaroidCard src={polaroidChilling} alt="Outdoor moment" />
            </motion.div>
          </div>

          {/* Polaroids — Mobile layout (stacked nicely) */}
          <div className="md:hidden flex flex-col items-center gap-4 mt-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <PolaroidCard src={polaroidCafe} alt="Cafe moment" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <PolaroidCard src={polaroidClose} alt="Close moment" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <PolaroidCard src={polaroidChilling} alt="Outdoor moment" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* Testimonials Section */}
      <motion.section
        className="relative py-24 px-4 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="max-w-4xl mx-auto">
          <motion.h2
            className="text-4xl sm:text-5xl font-light text-center mb-20 text-foreground italic"
            variants={itemVariants}
          >
            Stories from Our Community
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-card rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow border border-border"
                variants={itemVariants}
                whileHover={{ y: -4 }}
              >
                <p className="text-lg text-foreground/80 italic mb-6">
                  "{testimonial.quote}"
                </p>
                <p className="font-semibold text-foreground text-sm">
                  {testimonial.name}
                </p>
                <p className="text-xs text-foreground/60">The Blind Date</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
      {/* CTA Section */}
      <motion.section
        className="relative py-24 px-4 sm:px-6 lg:px-8 bg-primary/5"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <motion.h2
            className="text-4xl sm:text-5xl font-light mb-6 text-foreground italic"
            variants={itemVariants}
          >
            Ready for Something Real?
          </motion.h2>
          <motion.p
            className="text-lg text-foreground/70 mb-10 leading-relaxed"
            variants={itemVariants}
          >
            Join those who found genuine connections through authentic blind
            dating.
          </motion.p>
          <motion.div
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link to={"/city-selection"}>
              <button className="px-12 py-4 bg-primary text-primary-foreground rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                Begin Your Journey
              </button>
            </Link>
          </motion.div>
        </div>
      </motion.section>
      {/* Footer */}
      <Footer />
    </main>
  );
};

export { HomePage };
